import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";

import {
  createCoupon,
  editCoupon,
  fetchActiveCoupons,
  fetchCouponDetails,
} from "clients/packageManagementClient";
import { fetchMdsMappings } from "clients/mdsClient";
import { fetchPackagesByGatewayTenants } from "clients/omsClient";
import { fetchTenantsByVerticalId } from "clients/tenantManagementClient";
import {
  ACTIVE_COUPONS_DEFAULT_LIMIT,
  ACTIVE_COUPONS_DEFAULT_OFFSET,
  MDS_RESOURCE_TYPES,
  ZERO,
} from "constants/coupons";
import {
  COUPON_INFO_STALE_TIME,
  COUPON_LIST_STALE_TIME,
  COUPON_SCOPE_OPTIONS_STALE_TIME,
} from "constants/queryCacheTimes";
import {
  mapCurrencyOption,
  mapTenantOption,
  mapVerticalOption,
  normalizeTenantIdList,
} from "helpers/couponScopeOptions";
import { couponQueryKeys } from "store/backend/couponQueryKeys";

export const useBasicPackagesInfo = (gatewayTenantIds, enabled = true) => {
  const normalizedGatewayTenantIds = useMemo(
    () => normalizeTenantIdList(gatewayTenantIds),
    [gatewayTenantIds]
  );

  const shouldFetch =
    enabled && normalizedGatewayTenantIds.length > ZERO;

  return useQuery({
    queryKey: couponQueryKeys.packagesByTenants(normalizedGatewayTenantIds),
    queryFn: () => fetchPackagesByGatewayTenants(normalizedGatewayTenantIds),
    enabled: shouldFetch,
    staleTime: COUPON_LIST_STALE_TIME,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};

export const useActiveCouponsList = (enabled = true) =>
  useQuery({
    queryKey: couponQueryKeys.activeList(),
    queryFn: () =>
      fetchActiveCoupons({
        offset: ACTIVE_COUPONS_DEFAULT_OFFSET,
        limit: ACTIVE_COUPONS_DEFAULT_LIMIT,
      }),
    enabled,
    staleTime: COUPON_LIST_STALE_TIME,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

export const useCouponDetails = (couponCode, { enabled = true } = {}) =>
  useQuery({
    queryKey: couponQueryKeys.detail(couponCode),
    queryFn: () => fetchCouponDetails(couponCode),
    enabled: enabled && Boolean(couponCode?.trim()),
    staleTime: COUPON_INFO_STALE_TIME,
  });

export const useCouponLookup = () =>
  useMutation({
    mutationFn: (couponCode) => {
      const normalizedCode =
        typeof couponCode === "string"
          ? couponCode
          : String(couponCode?.couponCode ?? "").trim();
      return fetchCouponDetails(normalizedCode);
    },
  });

export const useCreateCoupon = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createCoupon,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: couponQueryKeys.activeList() });
    },
  });
};

export const useEditCoupon = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: editCoupon,
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({
        queryKey: couponQueryKeys.activeList(),
      });
      await queryClient.refetchQueries({
        queryKey: couponQueryKeys.detail(variables.couponCode),
      });
    },
  });
};

export const useCouponScopeVerticalOptions = (enabled = true) =>
  useQuery({
    queryKey: couponQueryKeys.scopeVerticals(),
    queryFn: () => fetchMdsMappings(MDS_RESOURCE_TYPES.VERTICAL),
    select: (verticalMappings) => verticalMappings.map(mapVerticalOption),
    enabled,
    staleTime: COUPON_SCOPE_OPTIONS_STALE_TIME,
  });

export const useCouponScopeCurrencyOptions = (enabled = true) =>
  useQuery({
    queryKey: couponQueryKeys.scopeCurrencies(),
    queryFn: () => fetchMdsMappings(MDS_RESOURCE_TYPES.CURRENCY),
    select: (currencyMappings) => currencyMappings.map(mapCurrencyOption),
    enabled,
    staleTime: COUPON_SCOPE_OPTIONS_STALE_TIME,
  });

export const useCouponScopeTenantsByVerticalId = (verticalId, enabled = true) => {
  const normalizedVerticalId = String(verticalId ?? "").trim();

  return useQuery({
    queryKey: couponQueryKeys.scopeTenants(normalizedVerticalId),
    queryFn: () => fetchTenantsByVerticalId(normalizedVerticalId),
    select: (tenants) => tenants.map(mapTenantOption),
    enabled: enabled && normalizedVerticalId.length > ZERO,
    staleTime: COUPON_LIST_STALE_TIME,
  });
};
